import { Carousel, type Slide } from "../ui/Carousel.tsx";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, api, mediaUrl, saveBooking, type ListingPage as L, type Quote } from "../api.ts";
import { addDays, money, today } from "../format.ts";
import { pick, useI18n } from "../i18n.tsx";
import { IconArrow, IconBed, IconDoor, IconGuests } from "../ui/Icons.tsx";
import { Pattern } from "../ui/Pattern.tsx";
import { Reveal } from "../ui/Reveal.tsx";

export function ListingPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  const nav = useNavigate();
  const [l, setL] = useState<L | null>(null);
  const [failed, setFailed] = useState(false);
  const [f, setF] = useState({ checkIn: params.get("in") ?? "", checkOut: params.get("out") ?? "", guests: params.get("g") ?? "2", name: "", phone: "", message: "" });
  const [quote, setQuote] = useState<Quote | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api<L>(`/listings/${id}`).then(setL).catch(() => setFailed(true)); }, [id]);

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setF({ ...f, [k]: e.target.value }); setQuote(null); };
  const datesOk = Boolean(f.checkIn && f.checkOut && f.checkOut > f.checkIn);

  async function getQuote() {
    setError(null);
    try { setQuote(await api<Quote>("/quote", { body: { listingId: id, checkIn: f.checkIn, checkOut: f.checkOut, guests: Number(f.guests) } })); } catch { setError(t("error")); }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<{ booking: { id: string; code: string }; accessToken: string }>("/guest/bookings", {
        body: { listingId: id, checkIn: f.checkIn, checkOut: f.checkOut, guests: Number(f.guests), message: f.message || undefined, guest: { name: f.name, phone: f.phone.replace(/\D/g, ""), locale } },
      });
      saveBooking({ id: r.booking.id, token: r.accessToken, code: r.booking.code });
      // The token travels in the URL fragment: it is never sent to a server or written to access logs.
      nav(`/b/${r.booking.id}#${r.accessToken}`);
    } catch (err) {
      setError(err instanceof ApiError && err.code === "dates_unavailable" ? t("problem.dates_taken") : err instanceof ApiError && err.code === "too_many_open_requests" ? err.message : t("error"));
    } finally { setBusy(false); }
  }

  if (failed) return <div className="wrap section"><div className="note"><Pattern /><p className="h2">{t("error")}</p><Link className="link" to="/">{t("back")}</Link></div></div>;
  if (!l) return <div className="wrap section"><div className="skel" style={{ aspectRatio: "16/9" }} aria-hidden="true" /></div>;

  const slides: Slide[] = [...l.photos.map((p) => ({ key: p.key, kind: "photo" as const })), ...(l.videos ?? []).map((v) => ({ key: v.key, kind: "video" as const }))];
  return (
    <div className="wrap">
      <div className="crumbs"><Link className="eyebrow link" to="/">← {t("back")}</Link></div>

      <Reveal>
        {slides.length === 0
          ? <div className="gallery"><div className="frame main"><Pattern /></div></div>
          : <Carousel slides={slides} labels={{ prev: t("gallery.prev"), next: t("gallery.next"), of: t("gallery.of") }} />}
      </Reveal>

      <div className="detail">
        <section>
          <span className="eyebrow">{l.cityKey}</span>
          <h1 className="h1" style={{ marginBlockStart: 12 }}>{pick(l.title, locale)}</h1>
          <div className="facts">
            <div className="fact"><IconDoor /><b className="num">{l.bedrooms}</b><span className="eyebrow">{t("listing.bedrooms")}</span></div>
            <div className="fact"><IconBed /><b className="num">{l.beds}</b><span className="eyebrow">{t("listing.beds")}</span></div>
            <div className="fact"><IconGuests /><b className="num">{l.maxGuests}</b><span className="eyebrow">{t("listing.guests")}</span></div>
          </div>
          {l.description && (
            <>
              <h2 className="h2" style={{ marginBlockEnd: 20 }}><span className="eyebrow"><span className="n num">01</span></span>{t("listing.about")}</h2>
              <p className="prose">{pick(l.description, locale)}</p>
            </>
          )}
        </section>

        <form className="panel" onSubmit={submit}>
          <h2 className="eyebrow" style={{ margin: 0 }}>{t("req.title")}</h2>
          <div className="price-line"><b className="num">{money(l.basePriceMinor, l.currency, locale)}</b><span className="muted">{t("perNight")}</span></div>

          <div className="pair">
            <label className="field"><span>{t("search.checkIn")}</span><input required type="date" min={today()} value={f.checkIn} onChange={set("checkIn")} /></label>
            <label className="field"><span>{t("search.checkOut")}</span><input required type="date" min={f.checkIn ? addDays(f.checkIn, 1) : today()} value={f.checkOut} onChange={set("checkOut")} /></label>
          </div>
          <label className="field"><span>{t("search.guests")}</span><input required type="number" inputMode="numeric" min={1} max={l.maxGuests} value={f.guests} onChange={set("guests")} /></label>

          <div className="quote" aria-live="polite">
            {quote?.problems.map((p) => <p key={p} className="err">{t(`problem.${p}`)}</p>)}
            {quote?.breakdown && <div className="kv"><span className="muted num">{quote.breakdown.nights} {t("req.nights")}</span><b className="num" style={{ fontWeight: 400 }}>{money(quote.breakdown.totalMinor, quote.currency, locale)}</b></div>}
            {!quote && <button type="button" className="link small muted" disabled={!datesOk} onClick={getQuote}>{t("req.quote")}</button>}
          </div>

          <label className="field"><span>{t("req.name")}</span><input required autoComplete="name" minLength={2} value={f.name} onChange={set("name")} /></label>
          <label className="field"><span>{t("req.phone")}</span><input required type="tel" inputMode="tel" autoComplete="tel" placeholder="212 6 12 34 56 78" pattern="[0-9 +]{8,18}" value={f.phone} onChange={set("phone")} /></label>
          <label className="field"><span>{t("req.message")}</span><textarea rows={2} maxLength={1000} value={f.message} onChange={set("message")} /></label>

          {error && <p className="err" role="alert">{error}</p>}
          <button className="btn" type="submit" disabled={busy || !datesOk || quote?.bookable === false}>{busy ? t("req.sending") : t("req.submit")} <IconArrow /></button>
          <p className="muted small" style={{ margin: "16px 0 0" }}>{t("req.note")}</p>
        </form>
      </div>
      <div style={{ height: 96 }} />
    </div>
  );
}
